import { createClient } from '@supabase/supabase-js';

// Lazy initialization - client is created on first access, not during module load
// Note: SupabaseError is imported dynamically to avoid circular dependency issues

const isProduction = import.meta.env.PROD;

// Inline sanitizer — mirrors SupabaseError.getUserFriendlyMessage() without importing
// errorHandling.js, which would create a circular dependency.
const sanitizeError = (message) => {
  if (!isProduction) return message;
  const networkPatterns = ['Failed to fetch', 'NetworkError', 'AuthRetryableFetchError',
    'ECONNREFUSED', 'ENOTFOUND', 'timeout', 'fetch is not defined'];
  if (networkPatterns.some(p => message?.toLowerCase().includes(p.toLowerCase()))) {
    return 'Connection issue. Please check your internet connection and try again.';
  }
  return 'An unexpected error occurred. Please try again.';
};
let _supabaseClient = null;
let _configChecked = false;

// Get the environment variables at runtime to prevent embedding in bundle
const getSupabaseConfig = () => {
  // Try multiple sources for environment variables
  let url = '';
  let key = '';
  
  // Try import.meta.env first (Vite's preferred method)
  try {
    url = import.meta.env.VITE_SUPABASE_URL || '';
    key = import.meta.env.VITE_SUPABASE_ANON_KEY || '';
  } catch (e) {
    // import.meta not available
  }
  
  // Fallback to window.ENV if injected at runtime (Netlify)
  if ((!url || !key) && typeof window !== 'undefined' && window.ENV) {
    url = url || window.ENV.SUPABASE_URL || window.ENV.VITE_SUPABASE_URL || '';
    key = key || window.ENV.SUPABASE_KEY || window.ENV.VITE_SUPABASE_ANON_KEY || '';
  }
  
  return { url, key };
};

// Create a mock client that shows helpful errors when config is missing
const createMockClient = () => {
  const errorPromise = () => Promise.resolve({ data: null, error: new Error('Supabase not configured') });
  
  return {
    auth: {
      getSession: errorPromise,
      getUser: errorPromise,
      signInWithPassword: () => { throw new Error('Supabase not configured'); },
      signUp: () => { throw new Error('Supabase not configured'); },
      signOut: () => Promise.resolve({ error: null }),
      onAuthStateChange: () => ({ data: { subscription: { unsubscribe: () => {} } } }),
      refreshSession: errorPromise,
    },
    from: () => ({
      select: () => ({
        eq: () => ({ single: errorPromise, maybeSingle: errorPromise }),
        single: errorPromise,
        maybeSingle: errorPromise,
      }),
      insert: () => ({ select: () => ({ single: errorPromise }) }),
      update: () => ({ eq: () => errorPromise }),
      delete: () => ({ eq: errorPromise }),
    }),
    channel: () => ({ on: () => ({ subscribe: () => ({}) }) }),
    removeChannel: () => {},
    functions: { invoke: errorPromise },
    rpc: errorPromise,
  };
};

// Create the real Supabase client with all configurations
const createRealClient = (url, key) => {
  return createClient(url, key, {
    auth: {
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: true,
      flowType: 'pkce',
      storageKey: 'va-sprouts-auth-storage'
    },
    global: {
      headers: {
        'X-Client-Info': 'va-sprouts-web-app'
      },
      fetch: (url, options = {}) => {
      // Enhanced fetch with better error logging and handling
      const startTime = Date.now();
      
      // Log auth request details for debugging 400 errors (dev only)
      if (!isProduction && url.includes('/auth/v1/token') && options.method === 'POST') {
        try {
          const body = JSON.parse(options.body || '{}');
          console.log('[Supabase Auth] Login attempt:', { 
            email: body.email?.substring(0, 3) + '***' || 'undefined',
            hasPassword: !!body.password,
            passwordLength: body.password?.length || 0,
            grantType: body.grant_type
          });
        } catch (e) {
          console.log('[Supabase Auth] Could not parse request body');
        }
      }
      
      if (!isProduction) console.log('[Supabase Fetch] Starting:', { url, method: options.method });
      
      return fetch(url, options)
        .then(response => {
          const duration = Date.now() - startTime;
          
          // Log response details for debugging 400 errors (dev only)
          if (!isProduction && !response.ok && response.status === 400 && url.includes('/auth/v1/token')) {
            console.error('[Supabase Auth] 400 Bad Request on login attempt', { 
              status: response.status,
              statusText: response.statusText,
              duration: `${duration}ms`,
              url
            });
          }

          // 406 Not Acceptable — most often caused by .single() returning 0 or >1 rows.
          // Tip: replace .single() with .maybeSingle() for optional lookups.
          if (!isProduction && !response.ok && response.status === 406) {
            const table = url.match(/\/rest\/v1\/([^?]+)/)?.[1] ?? 'unknown';
            console.warn(
              `[Supabase] 406 Not Acceptable on table "${table}". ` +
              'This usually means .single() found 0 or multiple rows. ' +
              'Use .maybeSingle() for optional lookups.',
              { url, duration: `${duration}ms` }
            );
          }
          
          if (!isProduction) console.log('[Supabase Fetch] Response received:', { 
            url, 
            status: response.status, 
            duration: `${duration}ms` 
          });
          return response;
        })
        .catch(error => {
          const duration = Date.now() - startTime;
          
          // Create enhanced error without importing SupabaseError to avoid circular deps
          const isNetworkError = 
            error?.message?.includes('Failed to fetch') || 
            error?.message?.includes('NetworkError') ||
            error?.message?.includes('ECONNREFUSED');
          
          if (!isProduction) {
            console.error('Supabase API fetch failed:', {
              url: url,
              error: error.message,
              type: error.name,
              isNetworkError,
              duration: `${duration}ms`,
              timestamp: new Date().toISOString()
            });
          }
          
          // Re-throw with a sanitized message in production to avoid leaking internals
          const rawMessage = `API fetch failed for ${url}: ${error.message}`;
          const enhancedError = new Error(sanitizeError(rawMessage));
          enhancedError.originalError = error;
          enhancedError.isNetworkError = isNetworkError;
          throw enhancedError;
        });
      }
    },
    realtime: {
      params: {
        eventsPerSecond: 25, // Must handle ~5 events/sec per active conversation (INSERT + trigger UPDATE)
        timeout: 10000,      // 10 second timeout
        heartbeat: 30000     // 30 second heartbeat
      },
      timeout: 10000,       // Connection timeout
      reconnectAfterMs: function(tries) {
        // Exponential backoff: 1s, 2s, 4s, 8s, then 10s
        return Math.min(Math.pow(2, tries) * 1000, 10000);
      }
    }
  });
};

// Lazy getter for the Supabase client - creates it on first access
const getSupabaseClient = () => {
  if (_supabaseClient !== null) {
    return _supabaseClient;
  }
  
  const { url, key } = getSupabaseConfig();
  
  if (!url || !key) {
    if (!_configChecked) {
      _configChecked = true;
      console.error('[Supabase] Missing environment variables. The app will not function correctly.');
      console.error('[Supabase] Please ensure VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY are set.');
    }
    _supabaseClient = createMockClient();
  } else {
    _supabaseClient = createRealClient(url, key);
  }
  
  return _supabaseClient;
};

// Create a proxy that lazily initializes the client on first property access
// This prevents any initialization code from running during module load
const supabaseProxy = new Proxy({}, {
  get(target, prop) {
    const client = getSupabaseClient();
    const value = client[prop];
    if (typeof value === 'function') {
      return value.bind(client);
    }
    return value;
  }
});

// Export the proxy as the supabase client
export const supabase = supabaseProxy;

// Utility function to check Supabase connectivity
export const checkSupabaseConnection = async () => {
  try {
    const client = getSupabaseClient();
    
    const { error: authError } = await client.auth.getSession();
    if (authError) {
      return {
        success: false,
        connected: false,
        error: sanitizeError(`Auth service error: ${authError.message}`),
        type: 'auth_error'
      };
    }
    
    return {
      success: true,
      connected: true,
      message: 'Supabase connection successful'
    };
  } catch (error) {
    const isNetworkError = 
      error?.message?.includes('Failed to fetch') || 
      error?.message?.includes('NetworkError');
      
    return {
      success: false,
      connected: false,
      error: isNetworkError 
        ? 'Cannot connect to Supabase. Your project may be paused or your internet connection may be unstable.'
        : sanitizeError(error?.message || 'Unknown error'),
      type: isNetworkError ? 'network_error' : 'unknown_error'
    };
  }
};

// Utility function to check if user exists in database
export const checkUserProfileExists = async (userId) => {
  try {
    const client = getSupabaseClient();
    const { data, error } = await client
      .from('user_profiles')
      .select('id, email, role')
      .eq('id', userId)
      .maybeSingle();
    
    if (error) {
      return { exists: false, error: sanitizeError(error.message) };
    }
    
    return { exists: !!data, profile: data };
  } catch (error) {
    return { exists: false, error: sanitizeError(error.message) };
  }
};

// Default export for backward compatibility - use supabaseProxy directly to avoid TDZ issues

export default supabaseProxy;
